import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';

import { Layout } from '@components/Layout';
import { Card } from '@components/Card';
import { Input } from '@components/Input';
import { Button } from '@components/Button';
import { LoadingSpinner } from '@components/LoadingSpinner';
import { useQuery } from '@tanstack/react-query';
import { getProducts } from '@lib/firebase/products';
import { useCartStore } from '@lib/store/cartStore';
import { formatCurrency } from '@lib/utils';
import { ProductCategory } from '@shared/types/product';
import { ROUTES } from '@lib/routes';
import type { Product } from '@shared/types/product';

const categoryLabels: Record<ProductCategory, string> = {
  [ProductCategory.BIKE]: 'Bikes',
  [ProductCategory.ACCESSORY]: 'Accessories',
  [ProductCategory.PART]: 'Parts',
  [ProductCategory.CLOTHING]: 'Clothing',
  [ProductCategory.SAFETY]: 'Safety Gear',
};

export function ProductsPage(): JSX.Element {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<ProductCategory | 'all'>('all');
  const [sortBy, setSortBy] = useState<'newest' | 'price-low' | 'price-high' | 'name'>('newest');
  const addToCart = useCartStore((state) => state.addItem);

  const { data: products, isLoading } = useQuery({
    queryKey: ['products'],
    queryFn: () => getProducts(),
  });

  const filteredAndSortedProducts = useMemo(() => {
    if (!products) return [];

    let filtered = products.filter((product) => {
      // Filter by active status
      if (!product.isActive) return false;

      // Filter by category
      if (selectedCategory !== 'all' && product.category !== selectedCategory) return false;

      // Filter by search query
      if (searchQuery) {
        const queryLower = searchQuery.toLowerCase();
        const matchesSearch =
          product.name.toLowerCase().includes(queryLower) ||
          product.description.toLowerCase().includes(queryLower) ||
          product.tags?.some((tag) => tag.toLowerCase().includes(queryLower));
        if (!matchesSearch) return false;
      }

      return true;
    });

    // Sort products
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'price-low':
          return a.price - b.price;
        case 'price-high':
          return b.price - a.price;
        case 'name':
          return a.name.localeCompare(b.name);
        case 'newest':
        default:
          return b.createdAt.getTime() - a.createdAt.getTime();
      }
    });

    return filtered;
  }, [products, selectedCategory, searchQuery, sortBy]);

  const handleAddToCart = (product: Product): void => {
    addToCart({
      productId: product.id,
      name: product.name,
      price: product.price,
      imageUrl: product.imageUrls[0],
    });
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-screen">
          <LoadingSpinner size="lg" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">Store</h1>

        {/* Filters */}
        <Card className="mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <Input
                placeholder="Search products..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <div className="w-full md:w-48">
              <select
                value={selectedCategory}
                onChange={(e) =>
                  setSelectedCategory((e.target.value as ProductCategory) || 'all')
                }
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
              >
                <option value="all">All Categories</option>
                {Object.values(ProductCategory).map((category) => (
                  <option key={category} value={category}>
                    {categoryLabels[category]}
                  </option>
                ))}
              </select>
            </div>
            <div className="w-full md:w-48">
              <select
                value={sortBy}
                onChange={(e) =>
                  setSortBy(
                    e.target.value as 'newest' | 'price-low' | 'price-high' | 'name'
                  )
                }
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
              >
                <option value="newest">Newest First</option>
                <option value="price-low">Price: Low to High</option>
                <option value="price-high">Price: High to Low</option>
                <option value="name">Name A-Z</option>
              </select>
            </div>
          </div>
        </Card>

        {/* Products Grid */}
        {filteredAndSortedProducts.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredAndSortedProducts.map((product) => (
              <Card key={product.id} hover>
                <Link to={`${ROUTES.CUSTOMER.PRODUCTS}/${product.id}`}>
                  {product.imageUrls && product.imageUrls.length > 0 && (
                    <img
                      src={product.imageUrls[0]}
                      alt={product.name}
                      className="w-full h-48 object-cover rounded-t-lg"
                    />
                  )}
                  <div className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex-1">
                        {product.name}
                      </h3>
                      {product.isFeatured && (
                        <span className="ml-2 px-2 py-1 text-xs rounded-full bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
                          Featured
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-3 line-clamp-2">
                      {product.description}
                    </p>
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <p className="text-lg font-bold text-gray-900 dark:text-white">
                          {formatCurrency(product.price)}
                        </p>
                        {product.compareAtPrice && product.compareAtPrice > product.price && (
                          <p className="text-sm text-gray-500 dark:text-gray-400 line-through">
                            {formatCurrency(product.compareAtPrice)}
                          </p>
                        )}
                      </div>
                      <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                        {categoryLabels[product.category]}
                      </span>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        fullWidth
                        onClick={(e) => {
                          e.preventDefault();
                          handleAddToCart(product);
                        }}
                        disabled={!product.inStock}
                      >
                        {product.inStock ? 'Add to Cart' : 'Out of Stock'}
                      </Button>
                    </div>
                  </div>
                </Link>
              </Card>
            ))}
          </div>
        ) : (
          <Card>
            <div className="text-center py-12">
              <p className="text-gray-600 dark:text-gray-400 text-lg">
                {searchQuery || selectedCategory !== 'all'
                  ? 'No products match your filters'
                  : 'No products available'}
              </p>
            </div>
          </Card>
        )}
      </div>
    </Layout>
  );
}

